import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WarehouseProvider } from './contexts/WarehouseContext';
import { I18nProvider } from './contexts/I18nProvider';
import { ThemeProvider } from './contexts/ThemeContext';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { VersionUpdateBanner } from './components/common/VersionUpdateBanner';
import { MaintenanceBanner } from './components/common/MaintenanceBanner';
import { ImpersonationBanner } from './components/common/ImpersonationBanner';
import { Login } from './components/auth/Login';
import { ForgotPassword } from './components/auth/ForgotPassword';
import { ResetPassword } from './components/auth/ResetPassword';
import { WarehouseMain } from './components/warehouse/WarehouseMain';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <MaintenanceBanner />
      <VersionUpdateBanner />
      <ImpersonationBanner />
      {children}
    </>
  );
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPassword />
        </PublicRoute>
      } />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/*" element={
        <ProtectedRoute>
          <WarehouseMain />
        </ProtectedRoute>
      } />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <WarehouseProvider>
            <I18nProvider>
              <ThemeProvider>
                <AppRoutes />
              </ThemeProvider>
            </I18nProvider>
          </WarehouseProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
